// !!! auto scroll bottom awal load
import axios from "axios";
import { toast } from "sonner";
import { useState, useRef, useEffect, type ChangeEvent, type KeyboardEvent } from "react";
import { MapPin } from "lucide-react";

import { useChatPresenter } from "../hooks/useChatPresenter";
import { useChatStore } from "@/stores/useChatStore";
import { useUI } from "../context/UIContext";
import { formatTimestamp } from "@/lib/utils";
import AchievementToast from "@/components/chatbot/AchievementToast";
import MatkulModal from "@/components/chatbot/MatkulModal";
import { ButtonMatkulModal, MessageBubble } from "@/components/chatbot/LittleElements";
import Sidebar from "@/components/chatbot/Sidebar";
import { useAuthStore } from "@/stores/useAuthStore";
import { BACKEND_URL, SUGGESTED_PROMPTS } from "@/constants";
import { useUIStore } from "@/stores/useUIStore";
import TourGuide from "@/components/TourGuide";
import RekomendasiResult from "@/components/chatbot/RekomendasiMsg";
import { elysiaErr } from "@/lib/elysiaErr";
import { useWinnerCheck } from "@/hooks/useWinnerCheck";

const isMobile = () => window.innerWidth < 640;

export default function ChatbotPage() {
    // store state
    const user = useAuthStore((state) => state.user);
    const token = useAuthStore((state) => state.token);
    const hasTour = useAuthStore((state) => state.hasTour);
    const setHasTour = useAuthStore((state) => state.setHasTour);
    const toastTag = useChatStore((s) => s.toastTag);
    const messages = useChatStore((s) => s.messages);
    const setSelectedMatkulItems = useAuthStore((state) => state.setSelectedMatkulItems);
    const setMenuOpen = useUIStore((s) => s.setMenuOpen);
    const dismissToast = useChatStore((s) => s.dismissToast);
    const setMsgCount = useUIStore((s) => s.setMsgCount);
    const showWinnerModal = useUIStore((s) => s.showWinnerModal);
    const showCalonWinnerModal = useUIStore((s) => s.showCalonWinnerModal);
    const setShowWinnerModal = useUIStore((s) => s.setShowWinnerModal);
    const setShowCalonWinnerModal = useUIStore((s) => s.setShowCalonWinnerModal);

    // logic presenter
    const { setMessages, isLoading, sendMessage } = useChatPresenter();
    const { setInputFocus, isNavbarVisible, setNavbarVisible } = useUI();
    useWinnerCheck();

    const hasMounted = useRef(false);
    const [startTour, setStartTour] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [hydrated, setHydrated] = useState(useAuthStore.persist.hasHydrated());

    const [input, setInput] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMKItemIds, setSelectedMKItemIds] = useState<number[]>([]);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // == Load to input my chat history == 
    // State untuk menyimpan riwayat string yang pernah dikirim
    const [history, setHistory] = useState<string[]>([]);
    // Index untuk melacak posisi riwayat (-1 artinya sedang mengetik pesan baru)
    const [historyIndex, setHistoryIndex] = useState<number>(-1);

    // Tunggu localStorage selesai di-rehydrate
    useEffect(() => {
        const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
        // Kalau ternyata sudah hydrated duluan sebelum listener terpasang
        if (useAuthStore.persist.hasHydrated()) setHydrated(true);
        return unsub;
    }, []);

    // ── Initial greeting ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!hydrated) return; // tunggu sampai state auth benar-benar sinkron dari localStorage

        // Tour guide — hanya jalan sekali seumur hidup (sampai localStorage dihapus)
        if (!hasTour) {
            setStartTour(true);
        }

        if (hasMounted.current) return;
        hasMounted.current = true;
        setLoaded(true);

        // tour guide
        if (!hasTour) {
            setStartTour(true);
        }

        if (messages.length > 0) return;
        setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    role: "assistant",
                    content: "Halo! Saya Akademik Bot untuk Mahasiswa.\nAda yang bisa saya bantu?",
                    timestamp: formatTimestamp(Date.now()),
                    showTourButton: true, // flag untuk render tombol tour
                },
            ]);
        }, 400);
    }, [hydrated]);

    // ── Scroll on new messages ─────────────────────────────────────────────────
    useEffect(() => {
        const lastMsg = messages[messages.length - 1];
        const behavior = isLoading || lastMsg?.role === "user" ? "auto" : "smooth";
        messagesEndRef.current?.scrollIntoView({ behavior, block: "nearest" });
    }, [messages, isLoading]);

    useEffect(() => { // otomatis berubah ketika messages length berubah
        setMsgCount(messages.filter((m) => m.role === "user").length);
    }, [messages.length]);

    // ── Matkul handlers ───────────────────────────────────────────────────────
    const handleConfirmMK = async () => {
        if (!selectedMKItemIds.length) return;

        const payload = {
            itemIds: selectedMKItemIds.map((item) => item),
        };
        try {
            if (user!.isTarget) { // target punya recomendasi
                const response = await axios.post(`${BACKEND_URL}/recom-target`,
                    payload, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                toast.success(response.data.message);
            } else {
                toast.success("NCF Recom (dummy) tested [tidak disimpan ke DB]!");
            }
            setSelectedMatkulItems(selectedMKItemIds); // dipakai di about

            // message juga harus berisi category
            setMessages((prev) => {
                const newMessages = [...prev];
                const lastMsgIndex = newMessages.length - 1;
                if (lastMsgIndex >= 0) {
                    newMessages[lastMsgIndex] = {
                        ...newMessages[lastMsgIndex],
                        selectedItemIds: selectedMKItemIds,
                        showMatkulModal: false,
                    };
                }
                return newMessages;
            });
        } catch (err) {
            elysiaErr(err);
            toast.error("Gagal menyimpan pilihan mata kuliah. Silakan coba lagi.");
        } finally {
            setSelectedMKItemIds([]);
            setModalOpen(false);
        }
    };

    // Fungsi pengiriman pesan
    const handleSend = () => {

        const trimmed = input.trim();
        if (!trimmed) return;

        sendMessage(trimmed);

        // Tambah ke riwayat (history)
        setHistory((prev) => [...prev, trimmed]);

        // Reset input dan index riwayat
        setInput('');
        setHistoryIndex(-1);
    };
    // Navigasi riwayat dengan tombol keyboard
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {

        // 1. Tekan ENTER untuk kirim
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
            return;
        }

        // Jika riwayat masih kosong, abaikan tombol panah
        if (history.length === 0) return;

        // 2. Tekan PANAH ATAS (Load pesan sebelumnya)
        if (e.key === 'ArrowUp') {
            e.preventDefault(); // Mencegah kursor pindah ke awal teks

            setHistoryIndex((prevIndex) => {
                // Jika baru mulai menekan panah atas, ambil pesan paling terakhir
                const nextIndex = prevIndex === -1 ? history.length - 1 : Math.max(0, prevIndex - 1);
                setInput(history[nextIndex]);
                return nextIndex;
            });
        }

        // 3. Tekan PANAH BAWAH (Navigasi kembali ke pesan yang lebih baru / input kosong)
        if (e.key === 'ArrowDown') {
            e.preventDefault(); // Mencegah kursor pindah ke akhir teks

            if (historyIndex === -1) return; // Sudah di input paling baru

            setHistoryIndex((prevIndex) => {
                const nextIndex = prevIndex + 1;

                if (nextIndex >= history.length) {
                    // Kembali ke keadaan input kosong/baru
                    setInput('');
                    return -1;
                } else {
                    setInput(history[nextIndex]);
                    return nextIndex;
                }
            });
        }
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
        const t = e.target;
        t.style.height = "24px";
        if (t.scrollHeight > 24)
            t.style.height = `${t.scrollHeight - 16}px`;

        // key history
        setInput(e.target.value);
        // Jika user mengetik manual saat melihat riwayat, reset index ke -1
        if (historyIndex !== -1) {
            setHistoryIndex(-1);
        }
    };

    if (!loaded) return null;

    return (
        <div className="w-full">
            <main className="flex-1 flex flex-col sm:flex-row gap-4 px-3 py-1 sm:p-6 sm:pt-0 max-w-250 mx-auto w-full transition-all duration-300 ease-in-out">
                {/* ── Sidebar ─────────────────────────────────────────────── */}
                <Sidebar
                    isNavbarVisible={isNavbarVisible}
                    setNavbarVisible={setNavbarVisible}
                />

                {/* ── Chat Area ──────────────────────────────────────────── */}
                <div
                    className="main-message relative flex-1 flex flex-col min-w-0 overflow-hidden"
                    // handle mobile responsive untuk virtual keyboard
                    style={{
                        maxHeight: isNavbarVisible
                            ? "calc(var(--vh, 1vh) * 100 - 60px)"
                            : "calc(var(--vh, 1vh) * 100 - 4px)",
                    }}
                >
                    {/* Messages container */}
                    <div className="mb-2 flex-1 min-h-0 p-4 overflow-y-auto flex flex-col gap-4 cb-scrollbar">
                        {messages.map((msg, msgIdx) => (
                            <div
                                key={msg.id}
                                className={`${msgIdx === 0 ? "mt-8" : ""} flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div className="flex flex-col gap-2 max-w-[85%] sm:max-w-[75%]">
                                    <MessageBubble msg={msg} />

                                    {/* ── BTN Start Tour — hanya di pesan pertama bot ── */}
                                    {msg.showTourButton && (
                                        <button
                                            onClick={() => setStartTour(true)}
                                            className="self-start flex items-center gap-2 px-3 py-2 border-2 border-black dark:border-neo-yellow
                                                bg-neo-yellow text-black text-xxs font-black uppercase tracking-wider
                                                shadow-[3px_3px_0_0_#000] dark:shadow-[3px_3px_0_0_#facc15]
                                                hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                                        >
                                            <MapPin size={11} strokeWidth={3} />
                                            Mulai Tour
                                        </button>
                                    )}

                                    {/* Btn Open Modal Select Matkul */}
                                    {msg.showMatkulModal && (
                                        <ButtonMatkulModal setModalOpen={setModalOpen} />
                                    )}

                                    {/* Tabel hasil submit di modal select matkul) */}
                                    {msg.selectedItemIds && (
                                        <RekomendasiResult selectedItemIds={msg.selectedItemIds} />
                                    )}
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-neo-yellow dark:bg-zinc-800 border-2 border-black dark:border-neo-yellow p-3 shadow-neo-sm flex gap-1">
                                    {[0, 1, 2].map((i) => (
                                        <div
                                            key={i}
                                            className="w-1.5 h-1.5 bg-black dark:bg-neo-yellow rounded-full animate-bounce"
                                            style={{ animationDelay: `${i * 0.2}s` }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* goto bottom, apa memang perlu buat elemen ini? */}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Suggested Chat */}
                    {messages.length <= 1 && !isLoading && (
                        <div className="shrink-0 px-4 pb-2 flex flex-wrap gap-2 justify-end">
                            {SUGGESTED_PROMPTS.map((prompt) => (
                                <button
                                    key={prompt}
                                    onClick={() => {
                                        sendMessage(prompt);
                                    }}
                                    className="px-2.5 py-1.5 text-xxs font-black uppercase tracking-wide border-2 border-black dark:border-neo-yellow
                    bg-white dark:bg-zinc-900 text-black dark:text-neo-yellow
                    shadow-neo-yellow dark:shadow-[2px_2px_0_0_#facc15]
                    hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input Section */}
                    <div className="mb-4 md:mb-0 shrink-0 bg-neo-bg dark:bg-zinc-950 pb-[env(safe-area-inset-bottom,0px)]">
                        <div className="flex flex-col gap-1">
                            <div className="neo-box flex items-stretch overflow-hidden bg-white dark:bg-zinc-900 dark:border-neo-yellow">
                                <textarea
                                    ref={textareaRef}
                                    value={input}
                                    onFocus={() => setInputFocus(true)}
                                    onBlur={() => setInputFocus(false)}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder={`Tulis pesan... ${isMobile() ? '' : '(Tekan uparrow untuk riwayat)'}`}
                                    className="flex-1 p-2 box-content bg-transparent outline-none resize-none text-sm
                                        dark:text-white placeholder:text-zinc-500 overflow-y-hidden"
                                    style={{ height: "24px" }}
                                />
                                <button
                                    onClick={() => {
                                        sendMessage(input);
                                        setInput("");
                                        textareaRef.current?.focus();
                                    }}
                                    disabled={isLoading || !input.trim()}
                                    className="neo-btn shrink-0 w-15 bg-black text-neo-yellow border-y-0 border-r-0 border-l-neo
                                        dark:border-neo-yellow dark:bg-neo-yellow dark:text-black rounded-none
                                        disabled:bg-gray-400 dark:disabled:bg-zinc-700"
                                >
                                    ↑
                                </button>
                            </div>
                            <p className="hidden sm:block text-xxs text-gray-500 dark:text-zinc-500 text-center uppercase tracking-tighter">
                                Shift + Enter for new line
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            {/* Modal */}
            {startTour && (
                <TourGuide
                    start={startTour}
                    setStartTour={setStartTour}
                    onOpenDrawer={() => setMenuOpen(true)}
                    onTourEnd={() => {
                        setStartTour(false);
                        setHasTour(true);
                    }}
                    onCloseDrawer={() => setMenuOpen(false)}
                />
            )}

            <AchievementToast tag={toastTag} onDismiss={dismissToast} />

            {modalOpen && (
                <MatkulModal
                    selectedMKItemIds={selectedMKItemIds}
                    setSelectedMKItemIds={setSelectedMKItemIds}
                    onConfirm={handleConfirmMK}
                    onClose={() => setModalOpen(false)}
                />
            )}

            <CompletedNoRewardModal
                open={showWinnerModal}
                onClose={() => setShowWinnerModal(false)}
            />
            <CalonWinnerModal
                open={showCalonWinnerModal}
                onClose={() => setShowCalonWinnerModal(false)}
                isTarget={user?.isTarget}
            />
        </div>
    );
}


function CompletedNoRewardModal({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-sm p-5 border-2 border-black dark:border-neo-yellow bg-white dark:bg-zinc-800 shadow-[4px_4px_0px_0px_#000] dark:shadow-[4px_4px_0px_0px_#facc15] flex flex-col gap-3 text-center">
                <span className="text-3xl">🙏</span>

                <span className="text-base font-black text-black dark:text-white">
                    Terima Kasih!
                </span>

                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                    Kamu telah berhasil menyelesaikan <span className="font-black text-black dark:text-neo-yellow">seluruh misi (100%)</span> di Akademik Bot. Kami sangat menghargai usaha dan partisipasi kamu!
                </span>

                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                    <span className="font-black">1st winner</span> sudah dipilih terlebih dahulu.
                </span>

                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 normal-case">
                    kamu masih punya kesempatan masuk undian 2nd winner Rp50k selama sudah memenuhi syarat. Terus pantau ya!
                </span>

                <button
                    onClick={onClose}
                    className="neo-btn mt-2 py-2 bg-black text-white dark:bg-neo-yellow dark:text-black text-xs font-black uppercase border-2 border-black shadow-neo-sm hover:bg-opacity-90 active:shadow-none active:translate-x-0.5 active:translate-y-0.5"
                >
                    Mengerti
                </button>
            </div>
        </div>
    );
}

function CalonWinnerModal({
    open,
    onClose,
    isTarget,
}: {
    open: boolean;
    onClose: () => void;
    isTarget?: boolean;
}) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-sm p-5 border-2 border-black dark:border-neo-yellow bg-white dark:bg-zinc-800 shadow-[4px_4px_0px_0px_#000] dark:shadow-[4px_4px_0px_0px_#facc15] flex flex-col gap-3 text-center">
                <span className="text-3xl">🎉</span>

                <span className="text-base font-black text-black dark:text-white">
                    Selamat, Kamu Unlock Akses!
                </span>


                <div className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                    Kamu berhasil menyelesaikan syarat dan kini punya{" "}
                    <span className="font-black text-black dark:text-neo-yellow">
                        akses ke model LLM
                    </span>{" "}
                    Akademik Bot.{" "}
                    {isTarget && (
                        <>
                            Kamu juga otomatis masuk sebagai{" "}
                            <span className="font-black text-black dark:text-neo-yellow">
                                calon 2nd winner
                            </span>{" "}
                            undian reward <span className="font-black">Rp50k</span>.
                        </>
                    )}
                </div>
                {isTarget && (
                    <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 normal-case">
                        Pemenang akan dipilih secara random dari seluruh calon yang memenuhi syarat. Pantau terus hasil undiannya ya!
                    </span>
                )}

                <button
                    onClick={onClose}
                    className="neo-btn mt-2 py-2 bg-black text-white dark:bg-neo-yellow dark:text-black text-xs font-black uppercase border-2 border-black shadow-neo-sm hover:bg-opacity-90 active:shadow-none active:translate-x-0.5 active:translate-y-0.5"
                >
                    Mengerti
                </button>
            </div>
        </div>
    );
}